import { createContext, type ReactNode, useContext, useState } from "react";
import { View } from "react-native";
import { Snackbar } from "react-native-paper";
import { FeedbackPortal } from "~/components/feedback/FeedbackPortal";
import { BaseText } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { QendoIcon, type QendoIconSet } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { useModal } from "~/providers/ModalProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { randomString } from "~/util/string";

export interface ToastInfo {
    content: string;
    action?: string;
    duration?: number;
    icon?: QendoIconSet;
}

type RealizedToast = {
    id: string;
} & ToastInfo;

export type FeedbackState = {
    saving: boolean | string;
    loading: boolean;
    error: string | null;
    setSaving(saving: boolean | string): void;
    setLoading(loading: boolean): void;
    addToast(toast: ToastInfo): void;
    setError(error: string | null): void;
};

export const FeedbackContext = createContext<FeedbackState>({
    saving: false,
    loading: false,
    error: null,
    setSaving(_saving) {
        return;
    },
    setLoading(_loading) {
        return;
    },
    addToast(_toast) {
        return;
    },
    setError(_error) {
        return;
    },
});

export function useFeedback() {
    const value = useContext(FeedbackContext);
    if (!value) {
        throw new Error("useFeedback must be wrapped in a <FeedbackProvider />");
    }
    return value;
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
    const { colours } = useTheme();
    const { topFullPageModal } = useModal();
    const [saving, setSaving] = useState<boolean | string>(false);
    const [loading, setLoading] = useState(false);
    const [toasts, setToasts] = useState<Record<string, RealizedToast>>({});
    const [error, setError] = useState<string | null>(null);

    const addToast = (toast: ToastInfo) => {
        const id = randomString();

        setToasts({ ...toasts, [id]: { id, icon: "checkmark-circle", duration: 3000, ...toast } });
    };

    const removeToast = (id: string) => {
        if (toasts[id]) {
            const { [id]: _remove, ...rest } = toasts;

            setToasts(rest);
        }
    };

    const value = {
        saving,
        loading,
        error,
        setSaving,
        setLoading,
        addToast,
        setError,
    };

    return (
        <FeedbackContext.Provider value={value}>
            {children}
            <Only
                not
                when={topFullPageModal}
                show={() => (
                    <FeedbackPortal saving={filled(saving)} loading={loading} error={error} setError={setError} />
                )}
            />
            {Object.values(toasts).map(({ id, content, action, icon, duration }) => (
                <Snackbar
                    visible
                    onDismiss={() => removeToast(id)}
                    key={id}
                    duration={duration}
                    action={action ? { label: action, onPress: () => removeToast(id) } : undefined}
                    theme={{
                        colors: {
                            surface: colours.toast.background,
                            onSurface: colours.toast.foreground,
                            inverseSurface: colours.toast.background,
                            inverseOnSurface: colours.toast.foreground,
                        },
                    }}
                >
                    <View style={[Flex.row, Items.center, Gap.xs]}>
                        {icon && <QendoIcon name={icon} size={20} color={colours.toast.foreground} />}
                        <BaseText size="xs" style={{ color: colours.toast.foreground }}>
                            {content}
                        </BaseText>
                    </View>
                </Snackbar>
            ))}
        </FeedbackContext.Provider>
    );
}
